import React, { useEffect, useState } from 'react';
import {
  TrendingUp, Swords, Calendar, Target, Handshake, Kanban, Radar, ShieldCheck,
} from 'lucide-react';

/*
 * VendorTabs — top-tab navigation for the Vendor Competitive Analysis
 * page. Six tabs, each a discrete view, syncs to URL hash so links
 * and reload preserve the active tab.
 *
 * Tab order matches the user's mental model:
 *   1. Trajectory   — where is this vendor going (snapshot + chart + heatmap)
 *   2. Competitive  — who's eating which categories (map + displacement)
 *   3. Renewals     — what's coming up (calendar with size + agency filters)
 *   4. Take-out     — competitive renewals 1-2yr + side-by-side (Phase 4)
 *   5. Partners     — who's selling our tech (ecosystem)
 *   6. Plan         — 30-60-90 + leadership rollup
 */

export const TABS = [
  { key: 'trajectory', label: 'Trajectory', icon: TrendingUp,
    sub: 'Footprint, growth curve, and agency × product penetration matrix' },
  { key: 'security', label: 'Security', icon: ShieldCheck,
    sub: 'Agency × security product coverage matrix with ZT pillar alignment' },
  { key: 'competitive', label: 'Competitive', icon: Swords,
    sub: 'Market share by category and displacement targets' },
  { key: 'renewals', label: 'Renewals', icon: Calendar,
    sub: 'Upcoming contract end-dates filtered by size and agency' },
  { key: 'takeout', label: 'Take-out', icon: Target,
    sub: 'Competitor renewals in the 12-24 month window with side-by-side counter-products' },
  { key: 'partners', label: 'Partners', icon: Handshake,
    sub: 'Resellers carrying this vendor across CA agencies' },
  { key: 'plan', label: '30-60-90 Plan', icon: Kanban,
    sub: 'Sales plays staged for execution with Master-Architect briefings' },
  { key: 'strategic', label: 'Initiatives', icon: Radar,
    sub: 'Strategic initiatives with linked renewals, milestones, and progress tracking' },
];

const HASH_PREFIX = '#tab=';

function readHash() {
  if (typeof window === 'undefined') return TABS[0].key;
  const h = window.location.hash;
  if (!h.startsWith(HASH_PREFIX)) return TABS[0].key;
  const candidate = h.slice(HASH_PREFIX.length);
  return TABS.find(t => t.key === candidate) ? candidate : TABS[0].key;
}

export function useVendorTabs() {
  const [activeKey, setActiveKey] = useState(readHash);

  // Listen for hash changes (back/forward navigation, link clicks).
  useEffect(() => {
    const onHash = () => setActiveKey(readHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const setTab = (key) => {
    if (!TABS.find(t => t.key === key)) return;
    setActiveKey(key);
    // Use replaceState rather than location.hash assignment so we
    // don't clobber the user's history with every tab click.
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.hash = `${HASH_PREFIX}${key}`;
      window.history.replaceState({}, '', url);
    }
  };

  return [activeKey, setTab];
}

export function VendorTabs({ activeKey, onChange }) {
  const active = TABS.find(t => t.key === activeKey) || TABS[0];
  return (
    <div className="vca-tabs">
      <div className="vca-tabs-bar" role="tablist" aria-label="Vendor analysis sections">
        {TABS.map(tab => {
          const Icon = tab.icon;
          const isActive = tab.key === activeKey;
          return (
            <button
              key={tab.key}
              role="tab"
              aria-selected={isActive}
              className={`vca-tab ${isActive ? 'is-active' : ''}`}
              onClick={() => onChange(tab.key)}
            >
              <Icon size={14} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>
      {active?.sub && (
        <p className="vca-tabs-sub">{active.sub}</p>
      )}
    </div>
  );
}

export default VendorTabs;
